import React, { ChangeEvent, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  CardMedia,
  IconButton,
  Input,
  MenuItem,
  TextField,
  Select,
  FormHelperText,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useCreateBadge, useUpdateBadge, useGetBadge } from "../../api/hooks/useBadge";
import { uploadService } from "../../api/services/uploadService";
import { showSuccess, showError } from "../../helpers/toast";
import { CRITERIA_TYPE, CRITERIA_TYPE_LABELS } from "../../constants/badge";
import type { Badge } from "../../api/types/badge.types";

const EditBadge = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const badgeData = (location.state && location.state.badge) as Badge | undefined;
  const badgeId = badgeData?._id;
  const isEditMode = !!badgeId;

  const [image, setImage] = useState<string>(badgeData?.image || "");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [uploadingImage, setUploadingImage] = useState(false);

  const [title, setTitle] = useState<string>(badgeData?.title || "");
  const [description, setDescription] = useState<string>(
    badgeData?.description || ""
  );
  // Handle both "type" (new API format) and "criteriaType" (legacy format)
  const initialType = badgeData?.type ?? badgeData?.criteriaType;
  const [criteriaType, setCriteriaType] = useState<number | "">(
    initialType ? Number(initialType) : ""
  );
  // Handle both "value" (new API format) and "targetValue" (legacy format)
  const initialValue = badgeData?.value ?? badgeData?.targetValue;
  const [targetValue, setTargetValue] = useState<string>(
    initialValue?.toString() || ""
  );

  // Form errors state
  const [errors, setErrors] = useState<{
    image?: string;
    title?: string;
    description?: string;
    criteriaType?: string;
    targetValue?: string;
  }>({});

  // Fetch badge data for edit mode
  const { data: badgeDetailData, isLoading: isLoadingBadge } = useGetBadge(
    badgeId || '',
    { enabled: isEditMode && !!badgeId }
  );

  // Update form fields when API data is fetched
  useEffect(() => {
    if (isEditMode && badgeDetailData?.data) {
      const badge = badgeDetailData.data;
      setTitle(badge.title || "");
      setDescription(badge.description || "");
      // Handle both "type" (new API format) and "criteriaType" (legacy format)
      const typeValue = badge.type ?? badge.criteriaType;
      setCriteriaType(typeValue ? Number(typeValue) : "");
      // Handle both "value" (new API format) and "targetValue" (legacy format)
      const value = badge.value ?? badge.targetValue;
      setTargetValue(value?.toString() || "");
      setImage(badge.image || "");
      // Clear any existing errors when loading API data
      setErrors({});
    }
  }, [isEditMode, badgeDetailData]);

  // Validation functions
  const validateImage = (): string => {
    if (!image) {
      return "Badge Image is required";
    }
    return "";
  };

  const validateTitle = (value: string): string => {
    if (!value.trim()) {
      return "Badge Title is required";
    }
    if (value.trim().length < 3) {
      return "Badge Title must be at least 3 characters";
    }
    if (value.trim().length > 100) {
      return "Badge Title must be less than 100 characters";
    }
    return "";
  };

  const validateDescription = (value: string): string => {
    if (!value.trim()) {
      return "Description is required";
    }
    if (value.trim().length < 10) {
      return "Description must be at least 10 characters";
    }
    if (value.trim().length > 500) {
      return "Description must be less than 500 characters";
    }
    return "";
  };

  const validateCriteriaType = (value: number | ""): string => {
    if (value === "" || value === undefined || value === null) {
      return "Criteria Type is required";
    }
    return "";
  };

  const validateTargetValue = (value: string): string => {
    if (!value.trim()) {
      return "Target Value is required";
    }
    if (isNaN(Number(value))) {
      return "Target Value must be a valid number";
    }
    if (Number(value) <= 0) {
      return "Target Value must be greater than 0";
    }
    if (!Number.isInteger(Number(value))) {
      return "Target Value must be a whole number";
    }
    return "";
  };

  // Validate all fields
  const validateAllFields = (): boolean => {
    const newErrors: typeof errors = {};

    const imageError = validateImage();
    if (imageError) newErrors.image = imageError;

    const titleError = validateTitle(title);
    if (titleError) newErrors.title = titleError;

    const descriptionError = validateDescription(description);
    if (descriptionError) newErrors.description = descriptionError;

    const criteriaTypeError = validateCriteriaType(criteriaType);
    if (criteriaTypeError) newErrors.criteriaType = criteriaTypeError;

    const targetValueError = validateTargetValue(targetValue);
    if (targetValueError) newErrors.targetValue = targetValueError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Create badge mutation
  const { mutate: createBadge, isPending: isCreating } = useCreateBadge({
    onSuccess: (response) => {
      showSuccess(response.message || "Badge created successfully");
      setTimeout(() => {
        navigate("/achievements");
      }, 100);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to create badge");
    },
  });

  // Update badge mutation
  const { mutate: updateBadge, isPending: isUpdating } = useUpdateBadge({
    onSuccess: (response) => {
      showSuccess(response.message || "Badge updated successfully");
      setTimeout(() => {
        navigate("/achievements");
      }, 100);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update badge");
    },
  });

  // Handle image change and upload immediately
  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      
      // Validate file type
      if (!file.type.includes("image")) {
        setErrors((prev) => ({ ...prev, image: "Please select a valid image file" }));
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setErrors((prev) => ({ ...prev, image: "Image size must be less than 5MB" }));
        return;
      }

      // Set preview image immediately
      setImageFile(file);
      setFileName(file.name);
      const previewUrl = URL.createObjectURL(file);
      setImage(previewUrl);
      
      // Clear error when valid file is selected
      if (errors.image) {
        setErrors((prev) => ({ ...prev, image: undefined }));
      }

      // Upload image to backend immediately
      try {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append("file", file);
        const uploadResponse = await uploadService.uploadFile(formData);
        
        let uploadedImageUrl = "";
        
        if (uploadResponse?.data) {
          // Handle different response formats
          if (typeof uploadResponse.data === "string") {
            uploadedImageUrl = uploadResponse.data;
          } else if (uploadResponse.data && typeof uploadResponse.data === "object") {
            const dataObj = uploadResponse.data as any;
            uploadedImageUrl = dataObj.url || dataObj.image || dataObj.path || "";
          }
        }

        if (!uploadedImageUrl) {
          setErrors((prev) => ({ ...prev, image: "Failed to upload image" }));
          setUploadingImage(false);
          // Reset image state on upload failure
          setImage("");
          setImageFile(null);
          setFileName("");
          return;
        }

        // Update image with uploaded URL
        setImage(uploadedImageUrl);
        // Clear the file reference since we now have the URL
        setImageFile(null);
        
      } catch (err: any) {
        const errorMessage = err?.response?.data?.message || "Failed to upload image";
        setErrors((prev) => ({ ...prev, image: errorMessage }));
        // Reset image state on upload failure
        setImage("");
        setImageFile(null);
        setFileName("");
      } finally {
        setUploadingImage(false);
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields before submission
    if (!validateAllFields()) {
      return;
    }

    // Image should already be uploaded, so use the image URL directly
    // Prepare payload
    const payload = {
      title: title.trim(),
      description: description.trim(),
      image: image,
      type: criteriaType as number,
      value: Number(targetValue),
    };

    // Call API - update if edit mode, create otherwise
    if (isEditMode && badgeId) {
      updateBadge({ id: badgeId, data: payload });
    } else {
      createBadge(payload);
    }
  };

  // Handle field changes with validation
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTitle(value);
    if (errors.title) {
      const error = validateTitle(value);
      setErrors((prev) => ({ ...prev, title: error || undefined }));
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDescription(value);
    if (errors.description) {
      const error = validateDescription(value);
      setErrors((prev) => ({ ...prev, description: error || undefined }));
    }
  };

  const handleCriteriaTypeChange = (e: any) => {
    const value = e.target.value === "" ? "" : Number(e.target.value);
    setCriteriaType(value);
    if (errors.criteriaType) {
      const error = validateCriteriaType(value);
      setErrors((prev) => ({ ...prev, criteriaType: error || undefined }));
    }
  };

  const handleTargetValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTargetValue(value);
    if (errors.targetValue) {
      const error = validateTargetValue(value);
      setErrors((prev) => ({ ...prev, targetValue: error || undefined }));
    }
  };

  // Handle blur events to validate fields
  const handleTitleBlur = () => {
    const error = validateTitle(title);
    setErrors((prev) => ({ ...prev, title: error || undefined }));
  };

  const handleDescriptionBlur = () => {
    const error = validateDescription(description);
    setErrors((prev) => ({ ...prev, description: error || undefined }));
  };

  const handleCriteriaTypeBlur = () => {
    const error = validateCriteriaType(criteriaType);
    setErrors((prev) => ({ ...prev, criteriaType: error || undefined }));
  };

  const handleTargetValueBlur = () => {
    const error = validateTargetValue(targetValue);
    setErrors((prev) => ({ ...prev, targetValue: error || undefined }));
  };

  return (
    <>
      <div className="main_title">
        <h1>{isEditMode ? "Edit Badge" : "Add Badge"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/achievements")}>
            Manage Achievements & Badges
          </button>{" "}
          - {isEditMode ? "Edit Badge" : "Add Badge"}
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <form onSubmit={handleSubmit}>
            <div className="gap_p">
              {/* Badge Image */}
              <div className="control_group w_33">
                <label>Badge Image*</label>
                {image ? (
                  <div className="upload_image">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia component="img" image={image} alt="Badge" />
                        {uploadingImage && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(0, 0, 0, 0.5)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "4px",
                            }}
                          >
                            <span style={{ color: "white", fontWeight: "bold" }}>
                              Uploading...
                            </span>
                          </div>
                        )}
                      </figure>
                      <IconButton
                        aria-label="Remove uploaded image"
                        onClick={() => {
                          setImage("");
                          setImageFile(null);
                          setFileName("");
                          if (errors.image) {
                            setErrors((prev) => ({ ...prev, image: undefined }));
                          }
                        }}
                        size="small"
                        disabled={uploadingImage}
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>
                  </div>
                ) : (
                  <label className="upload_image" htmlFor="icon-button-file">
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg,image/jpg" }}
                      onChange={handleImageChange}
                      disabled={uploadingImage}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/badge_placeholder.png"
                          alt="placeholder"
                          style={{ objectFit: "contain" }}
                        />
                        <span className="upload_img_icon">
                          <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
                {errors.image && (
                  <FormHelperText error style={{ marginTop: "4px", marginLeft: "0px" }}>
                    {errors.image}
                  </FormHelperText>
                )}
                {uploadingImage && !errors.image && (
                  <FormHelperText style={{ marginTop: "4px", marginLeft: "0px", color: "#1976d2" }}>
                    Uploading image...
                  </FormHelperText>
                )}
              </div>
              <div className="control_group w_50"/>
              {/* Title */}
              <div className="control_group w_33">
                <label>Badge Title*</label>
                <TextField
                  placeholder="Enter Badge Title"
                  fullWidth
                  value={title}
                  onChange={handleTitleChange}
                  onBlur={handleTitleBlur}
                  error={!!errors.title}
                />
                {errors.title && (
                  <FormHelperText error style={{ marginTop: "4px", marginLeft: "0px" }}>
                    {errors.title}
                  </FormHelperText>
                )}
              </div>

              {/* Criteria Type */}
              <div className="control_group w_33">
                <label>Criteria Type*</label>
                <FormControl fullWidth error={!!errors.criteriaType}>
                  <Select
                    value={criteriaType}
                    onChange={handleCriteriaTypeChange}
                    onBlur={handleCriteriaTypeBlur}
                    displayEmpty
                    renderValue={(selected: number | "") => {
                      if (selected === "" || selected === undefined || selected === null) {
                        return <span style={{ color: "#999" }}>Select Criteria</span>;
                      }
                      if (typeof selected === "number" && selected in CRITERIA_TYPE_LABELS) {
                        return CRITERIA_TYPE_LABELS[selected];
                      }
                      return <span style={{ color: "#999" }}>Select Criteria</span>;
                    }}
                  >
                    <MenuItem value="">
                      <span style={{ color: "#999" }}>Select Criteria</span>
                    </MenuItem>
                    <MenuItem value={CRITERIA_TYPE.WORKOUTS}>
                      {CRITERIA_TYPE_LABELS[CRITERIA_TYPE.WORKOUTS]}
                    </MenuItem>
                    <MenuItem value={CRITERIA_TYPE.STREAKS}>
                      {CRITERIA_TYPE_LABELS[CRITERIA_TYPE.STREAKS]}
                    </MenuItem>
                    <MenuItem value={CRITERIA_TYPE.TENURE}>
                      {CRITERIA_TYPE_LABELS[CRITERIA_TYPE.TENURE]}
                    </MenuItem>
                    <MenuItem value={CRITERIA_TYPE.PROGRAMS}>
                      {CRITERIA_TYPE_LABELS[CRITERIA_TYPE.PROGRAMS]}
                    </MenuItem>
                  </Select>
                  {errors.criteriaType && (
                    <FormHelperText error style={{ marginTop: "4px", marginLeft: "0px" }}>
                      {errors.criteriaType}
                    </FormHelperText>
                  )}
                </FormControl>
              </div>

              {/* Target Value */}
              <div className="control_group w_33">
                <label>Target Value*</label>
                <TextField
                  placeholder="Enter Number"
                  fullWidth
                  type="number"
                  value={targetValue}
                  onChange={handleTargetValueChange}
                  onBlur={handleTargetValueBlur}
                  error={!!errors.targetValue}
                />
                {errors.targetValue && (
                  <FormHelperText error style={{ marginTop: "4px", marginLeft: "0px" }}>
                    {errors.targetValue}
                  </FormHelperText>
                )}
              </div>

              {/* Description */}
              <div className="control_group w_100">
                <label>Description*</label>
                <TextField
                  placeholder="Enter Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={description}
                  onChange={handleDescriptionChange}
                  onBlur={handleDescriptionBlur}
                  error={!!errors.description}
                />
                {errors.description && (
                  <FormHelperText error style={{ marginTop: "4px", marginLeft: "0px" }}>
                    {errors.description}
                  </FormHelperText>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="form_btn">
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isCreating || isUpdating || isLoadingBadge || uploadingImage}
              >
                {isLoadingBadge
                  ? "Loading..."
                  : uploadingImage
                  ? "Uploading..."
                  : isCreating || isUpdating
                  ? "Saving..."
                  : isEditMode
                  ? "Update Badge"
                  : "Save Badge"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditBadge;
